/**
 * Cart extension sync — 延長明細と本体明細の日程を連動させる（NOB-1488 / #2649）
 *
 * rental-calendar.js は「予約時の日数追加（延長）」を選ぶと、1回の /cart/add.js で
 *   - 本体明細: 延長込みの終了日（レンタル終了日 / _finish_iso8601）＋ `延長` `_extension_days`
 *   - 延長明細: `_is_extension=true` `_parent_variant_id` `_start_iso8601` …
 * の2行を同時にカートへ入れる。ところが Dawn のカート操作（cart.js）は対象行の
 * quantity を変えるだけで、もう一方の行には何もしない。顧客が延長行だけを
 * 削除すると、請求は基本料金だけなのに本体の表示は延長込みの終了日のまま残り
 * （#2649: 表示 9/26・請求は1泊2日）、決済後に TechRental-ops の延長ガード
 * （NOB-1067 `applyExtensionGuard`）が黙って終了日を1日巻き戻す — 顧客は注文画面で
 * 見た日付と違う返却日を突きつけられる。
 *
 * このスクリプトはカート層でその不整合をその場で解消する:
 *
 *   延長行の数量を本体へ 1対1（quantity 単位）で割り当て、割り当ての結果だけを見る。
 *   1. 割り当てが足りない本体（未課金の延長 claim）
 *      → 本体明細のプロパティを「延長なし」の形に書き戻す。終了日を延長日数ぶん
 *        戻し、`延長` と `_extension_days` を外す。他のプロパティ（_validated_at 等）
 *        は保つ。Shopify の /cart/change.js は `properties` を受け付け、渡した内容で
 *        明細のプロパティを丸ごと置き換える（行の key は変わる）。
 *        一部だけ足りない（本体 quantity 2 に延長 1）時は自動では直さず、揃うまで
 *        チェックアウトを塞いで顧客に案内する（意図が二通りに読めるため）。
 *   2. 割り当てられなかった延長行の数量（親のいない延長）
 *      → 何の日数も表さない請求だけなので、全部余れば削除、一部余れば減らす。
 *
 * 対応関係の判定（extensionBelongsTo）は ops 側の延長ガードと同じ `_parent_variant_id`
 * = 本体の variant_id を軸にし、本体が延長を主張していること、開始日・延長日数・
 * 終了日が両方にあれば一致することを求める（同じ機種を別日程／別日数で複数入れた
 * 時の取り違え防止）。対象はカレンダー由来（`_parent_variant_id` あり）の延長行のみで、
 * セルフ延長（NOB-1193・`_parent_rental_id`）の行は触らない。ここで書き戻した本体明細は ops 側から見れば
 * 「延長を主張していない」普通の明細になるので、ガードは何もしない。ガードは
 * このスクリプトが動かなかった経路（JS無効・/cart/change 直叩き・旧アセット
 * キャッシュ）の保険として残る。
 *
 * 走るタイミング: カート/ドロワーの描画時と、カート内容の変更（cartUpdate）後。
 * 書き戻し後は cartUpdate を発行して Dawn の cart-items に再描画させ、
 * 顧客に何が起きたかを通知ブロック（チェックアウトボタン直前）で知らせる。
 * 顧客が「延長を外した」意図は尊重する — 延長行を自動で戻したりはしない。
 *
 * 純粋ロジック（planReconcile など）は Node の tests/cart-extension-sync.test.js で
 * 回すため、document の無い環境では DOM 初期化をスキップして module.exports で公開する
 * （cart-deadline-guard.js と同じ作法）。
 */
(function () {
  'use strict';

  var DAY_MS = 24 * 60 * 60 * 1000;
  var NOTICE_CLASS = 'cart-extension-sync-notice';
  var SOURCE = 'cart-extension-sync';

  function propsOf(item) {
    return (item && item.properties) || {};
  }

  function isExtensionLine(item) {
    return propsOf(item)['_is_extension'] === 'true';
  }

  // 本体明細が主張する延長日数。無効・0以下は 0（主張なし）。
  function claimedExtensionDays(item) {
    var n = parseInt(propsOf(item)['_extension_days'], 10);
    return isNaN(n) || n <= 0 ? 0 : n;
  }

  function variantIdOf(item) {
    return String(item.variant_id || item.id || '');
  }

  // カレンダー由来の延長明細か。`_parent_variant_id` を持つものだけをこのスクリプトの
  // 対象にする。セルフ延長（NOB-1193・ops rental-self-service）は `_is_extension` を
  // 持つが `_parent_variant_id` は無く `_parent_rental_id` で親を指す — Draft Order の
  // 請求書URLで決済されカートには入らないが、万一カートに現れても触らない。
  function isCalendarExtension(item) {
    return isExtensionLine(item) && !!propsOf(item)['_parent_variant_id'];
  }

  // 延長明細 ext が本体明細 main の「延長の請求」として対応するか。
  //   - 本体が延長を主張している（_extension_days > 0）— 延長なしの本体は親にならない。
  //     同じ機種・同じ日程で「延長なし」と「延長あり」を1件ずつ入れ、延長ありの本体だけ
  //     消した時に、延長なしの本体を親と見て延長行を残さないため（Codex P1）
  //   - _parent_variant_id = 本体の variant_id（ops 側 extension-guard.ts と同じ軸）
  //   - 開始日（_start_iso8601）が両方にあれば一致
  //   - 延長日数（ext 側の _extension_days）があれば本体の主張と一致 — +2日の本体に
  //     +1日の延長行を割り当てると、1日分の請求で2日延びる（Codex P1）
  //   - 終了日（_finish_iso8601）が両方にあれば一致（同上の別表現）
  function extensionBelongsTo(ext, main) {
    var ep = propsOf(ext);
    var mp = propsOf(main);
    var claim = claimedExtensionDays(main);
    if (claim === 0) return false;
    if (!ep['_parent_variant_id']) return false;
    if (String(ep['_parent_variant_id']) !== variantIdOf(main)) return false;
    if (ep['_start_iso8601'] && mp['_start_iso8601'] &&
        ep['_start_iso8601'] !== mp['_start_iso8601']) {
      return false;
    }
    var extDays = parseInt(ep['_extension_days'], 10);
    if (!isNaN(extDays) && extDays > 0 && extDays !== claim) return false;
    if (ep['_finish_iso8601'] && mp['_finish_iso8601'] &&
        ep['_finish_iso8601'] !== mp['_finish_iso8601']) {
      return false;
    }
    return true;
  }

  // ISO8601 インスタントを days 日戻す。壊れていれば null。
  function shiftIso(iso, days) {
    var t = Date.parse(iso);
    if (isNaN(t)) return null;
    return new Date(t - days * DAY_MS).toISOString();
  }

  // 表示用日付（YYYY.MM.DD、rental-calendar.js の formatDateKanji 形式）を days 日戻す。
  // UTC 算術で行い、閲覧環境の DST に影響されないようにする。壊れていれば null。
  function shiftVisibleDate(value, days) {
    var m = String(value).match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
    if (!m) return null;
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]) - days * DAY_MS);
    var mm = ('0' + (d.getUTCMonth() + 1)).slice(-2);
    var dd = ('0' + d.getUTCDate()).slice(-2);
    return d.getUTCFullYear() + '.' + mm + '.' + dd;
  }

  // 本体明細のプロパティを「延長なし」の形に書き戻したものを返す。
  // 終了日を戻せる材料（_finish_iso8601 か レンタル終了日）が無い／壊れている時は
  // null（触らない＝ops 側ガードに委ねる。中途半端に `延長` だけ外すと、請求も
  // 表示も合っていないのに ops 側の検知まで消えてしまう）。
  function rolledBackProperties(props, days) {
    var out = {};
    var touched = false;
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      if (k === '延長' || k === '_extension_days') continue;
      out[k] = props[k];
    }
    if (props['_finish_iso8601']) {
      var iso = shiftIso(props['_finish_iso8601'], days);
      if (!iso) return null;
      out['_finish_iso8601'] = iso;
      touched = true;
    }
    if (props['レンタル終了日']) {
      var vis = shiftVisibleDate(props['レンタル終了日'], days);
      if (!vis) return null;
      out['レンタル終了日'] = vis;
      touched = true;
    }
    return touched ? out : null;
  }

  // カート（/cart.js の形）から、整合を取るための操作列を作る。
  //
  // 延長行の数量を本体へ 1対1（quantity 単位）で割り当て、割り当ての結果だけを見る:
  //   本体側: 割り当てが quantity に足りない分は「延長なし」に書き戻す
  //     全部足りない → { type: 'update', kind: 'rollback', key, quantity, properties }
  //     一部足りない → { type: 'block',  kind: 'partial', key, need, covered }
  //       本体2点に延長1点のような状態は、顧客が「2点とも延長したい」のか「1点に戻したい」
  //       のか分からない。行を分けて片方だけ延長なしにする案は、同じ開始日で終了日の違う
  //       2行を作り、cart-deadline-guard.js の在庫検証（機種×開始×終了で合算）が
  //       両者を別需要として通す穴を開ける上、減らす→足すの2操作が途中で止まると
  //       数量が黙って減る（Codex P1/P2）。ここでは何も変えず、チェックアウトを塞いで
  //       顧客に揃えてもらう（延長を消せば次の周回で rollback に落ちる）。
  //   延長側: 割り当てられなかった数量は請求だけの行なので減らす
  //     全部余り → { type: 'remove', kind: 'orphan', key }
  //     一部余り → { type: 'update', kind: 'excess', key, quantity: 割り当て数 }
  // 順序: block → update → remove。remove を最後に置くのは、カートが空になりうる応答
  // （/cart/change.js は item_count を返す）で描き直すため。block は通信しない。
  // どの操作も1リクエストで完結するので、途中で止まっても半端な状態は残らない。
  //
  // 同じ機種・同じ日程を2回追加すると Shopify は _validated_at の違いで別行として
  // 保持するため、本体2行＋延長1行のような状態が作れる。存在の有無（boolean）で見ると
  // 両方が「課金済み」に見えて、延長1件分の請求で2件分の延長日程が通る（Codex P1）。
  function planReconcile(cart) {
    var items = (cart && cart.items) || [];
    var blocks = [];
    var updates = [];
    var removes = [];
    var mains = [];
    var exts = [];
    items.forEach(function (item) {
      if (isExtensionLine(item)) {
        if (isCalendarExtension(item)) exts.push(item);
        return;
      }
      mains.push(item);
    });

    var remaining = exts.map(function (ext) { return ext.quantity || 1; });

    // 本体は「同じ請求単位」でまとめて見る: 同じバリアント・開始日・延長日数・終了日の
    // 本体は、別行（_validated_at 違い）でも1つのグループ。グループの合計 quantity に
    // 対して延長行を割り当て、足りない時の扱いをグループ単位で決める。行ごとに
    // 決めると、本体2行（各1点）に延長1点で片方だけ書き戻し、同じ開始日で終了日の
    // 違う2行＝在庫検証（機種×開始×終了で合算）の穴になる形を作ってしまう（Codex P1）。
    var groups = [];
    var groupByKey = {};
    mains.forEach(function (main) {
      var days = claimedExtensionDays(main);
      if (days === 0) return;
      var mp = propsOf(main);
      var gkey = [variantIdOf(main), mp['_start_iso8601'] || '', days, mp['_finish_iso8601'] || ''].join('|');
      var g = groupByKey[gkey];
      if (!g) {
        g = { mains: [], days: days, need: 0 };
        groupByKey[gkey] = g;
        groups.push(g);
      }
      g.mains.push(main);
      g.need += main.quantity || 1;
    });

    groups.forEach(function (g) {
      var covered = 0;
      for (var i = 0; i < exts.length && covered < g.need; i++) {
        if (remaining[i] <= 0 || !extensionBelongsTo(exts[i], g.mains[0])) continue;
        var take = Math.min(remaining[i], g.need - covered);
        remaining[i] -= take;
        covered += take;
      }
      if (covered >= g.need) return;
      var title = g.mains[0].product_title || g.mains[0].title || 'レンタル商品';
      if (covered === 0) {
        g.mains.forEach(function (main) {
          // 終了日の材料が無い／壊れている時は触らない（ops 側ガードへ）
          var props = rolledBackProperties(propsOf(main), g.days);
          if (!props) return;
          updates.push({
            type: 'update', kind: 'rollback', key: main.key, quantity: main.quantity || 1,
            properties: props, title: title, days: g.days, newEnd: props['レンタル終了日'] || null
          });
        });
        return;
      }
      // block は書き戻し用のプロパティを使わないので、材料が無くても塞ぐ（CodeRabbit）
      blocks.push({
        type: 'block', kind: 'partial', key: g.mains[0].key,
        keys: g.mains.map(function (m) { return m.key; }),
        title: title, need: g.need, covered: covered, days: g.days
      });
    });

    exts.forEach(function (ext, i) {
      var total = ext.quantity || 1;
      var consumed = total - remaining[i];
      var title = ext.product_title || ext.title || '延長';
      if (consumed >= total) return;
      if (consumed === 0) {
        removes.push({ type: 'remove', kind: 'orphan', key: ext.key, title: title });
      } else {
        updates.push({
          type: 'update', kind: 'excess', key: ext.key, quantity: consumed,
          title: title, total: total
        });
      }
    });

    return blocks.concat(updates, removes);
  }

  // 顧客向けの説明文。何が起きたか（block は何をしてほしいか）と、
  // 延長したい場合の戻り道を1件ずつ。
  function noticeLines(ops) {
    var lines = [];
    ops.forEach(function (op) {
      if (op.kind === 'rollback') {
        lines.push('延長が外されたため、「' + op.title + '」のレンタル終了日を' +
          (op.newEnd ? ' ' + op.newEnd + ' ' : '') + 'に戻しました（' + op.days +
          '日分）。延長をご希望の場合は、商品ページから日程を選び直してください。');
      } else if (op.kind === 'partial') {
        lines.push('「' + op.title + '」は ' + op.need + ' 点ですが、延長（+' + op.days +
          '日）の料金は ' + op.covered + ' 点分です。延長の数量を ' + op.need +
          ' 点にするか、本体を ' + op.covered + ' 点に戻してください。' +
          '延長が不要な場合は延長を削除すると、日程が延長なしに戻ります。' +
          'そろうまでご購入手続きへ進めません。');
      } else if (op.kind === 'orphan') {
        lines.push('対象のレンタル本体がカートに無いため、「' + op.title + '」をカートから外しました。');
      } else if (op.kind === 'refreshed') {
        lines.push('カートの内容を最新の状態に更新しました。日程と数量をご確認ください。');
      } else if (op.kind === 'error') {
        // 何が失敗したかで戻り道を変える。「請求どおりの日程で届く」と言えるのは
        // 書き戻し（日程だけの問題）の時だけ。延長行の削除・減量の失敗は請求側の
        // 問題なので、顧客に延長を直してもらう。
        if (op.failedKind === 'rollback' && op.chargeUnresolved) {
          // 後ろに延長行の削除・減量が残っている＝請求側も未解決
          lines.push('カートの日程を更新できませんでした。お手数ですがページを再読み込みし、' +
            '日程とご請求内容（延長の有無・数量）をご確認のうえご注文ください。');
        } else if (op.failedKind === 'rollback') {
          lines.push('カートの日程を更新できませんでした。お手数ですがページを再読み込みしてください。' +
            '再読み込み後も表示が変わらない場合は、そのままご注文いただいても、' +
            'ご請求どおりの日程（延長なし）でお届けします。');
        } else if (op.failedKind === 'orphan') {
          lines.push('対象のレンタル本体がカートに無い「' + (op.title || '延長') +
            '」を外せませんでした。お手数ですがページを再読み込みし、' +
            '残っている場合はカートから削除してからご注文ください。');
        } else if (op.failedKind === 'excess') {
          lines.push('「' + (op.title || '延長') + '」の数量を対象のレンタル本体に合わせられませんでした。' +
            'お手数ですがページを再読み込みし、残っている場合は延長の数量を本体の数量に合わせてからご注文ください。');
        } else {
          lines.push('カートの内容を確認できませんでした。お手数ですがページを再読み込みしてください。');
        }
      } else if (op.kind === 'excess') {
        lines.push('「' + op.title + '」は対象のレンタル本体の数量に合わせて ' + op.quantity +
          ' 点にしました（' + op.total + ' 点のうち ' + (op.total - op.quantity) +
          ' 点は対象のレンタルがありません）。');
      }
    });
    return lines;
  }

  // ── Node(テスト)用エクスポート ──
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = {
        isExtensionLine: isExtensionLine,
        isCalendarExtension: isCalendarExtension,
        claimedExtensionDays: claimedExtensionDays,
        extensionBelongsTo: extensionBelongsTo,
        shiftIso: shiftIso,
        shiftVisibleDate: shiftVisibleDate,
        rolledBackProperties: rolledBackProperties,
        planReconcile: planReconcile,
        noticeLines: noticeLines
      };
    }
    return;
  }

  // ── ここから DOM / ネットワーク ──

  var inFlight = false;
  var queued = false;


  // Dawn の cart-items / cart-drawer-items が持つ「変更後に描き直すセクション」
  // （{ id, section, selector }）。/cart/change.js に sections を頼み、返ってきた
  // HTML で cart.js の updateQuantity と同じ場所を描き直す。cartUpdate を発行する
  // だけだと、カートページの CartItems.onCartUpdate は明細（main-cart-items）しか
  // 取り直さず、フッター（小計・チェックアウトボタン・Shop Pay）が変更前のまま
  // 残る — 親のいない延長行を外して合計が変わった／カートが空になった時に実害
  // （レビュー指摘・Codex P2）。
  function cartItemsElement() {
    return document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
  }

  // Dawn の CartItems / CartDrawerItems.getSectionsToRender() と同じ内容を DOM から
  // 組み立てる予備。初回表示では、head の defer スクリプトであるこちらが、セクション側で
  // 読み込まれる cart.js より先に走りうる（custom element が未定義）。その時に sections
  // を頼めないと、明細の削除・減量の応答にフッターの HTML が無く描き直せない（Codex P2）。
  function fallbackSectionsToRender() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) {
      var list = [{ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' }];
      if (footer && footer.dataset.id) {
        list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
      }
      return list;
    }
    if (document.getElementById('CartDrawer')) {
      return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
    }
    return [];
  }

  function sectionsToRender() {
    var el = cartItemsElement();
    if (el && typeof el.getSectionsToRender === 'function') {
      try {
        var list = el.getSectionsToRender();
        if (list && list.length) return list;
      } catch (e) { /* 予備へ */ }
    }
    return fallbackSectionsToRender();
  }

  function renderSections(state) {
    if (!state || !state.sections) return;
    sectionsToRender().forEach(function (section) {
      var html = state.sections[section.section];
      var container = document.getElementById(section.id);
      if (!html || !container) return;
      var target = container.querySelector(section.selector) || container;
      var source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
    if (typeof state.item_count === 'number') {
      var empty = state.item_count === 0;
      [cartItemsElement(), document.getElementById('main-cart-footer'), document.querySelector('cart-drawer')]
        .forEach(function (el) {
          if (el) el.classList.toggle('is-empty', empty);
        });
    }
    // セクション差し替えでチェックアウトボタンが新品（有効・印なし）になるので、
    // 塞いでいる最中ならここで塞ぎ直す（CodeRabbit）。
    applyGate();
  }

  // Section Rendering API（GET ?sections=a,b → {a: html, b: html}）で現在のカートの
  // セクションを取り直す。変更リクエストを伴わずに描き直したい時に使う。
  function refreshSections(cart) {
    var sections = cartSections();
    var state = { item_count: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined };
    if (sections.length === 0) return Promise.resolve(state);
    // 取れなかった時は失敗として投げる — 空のまま「更新しました」と言うと、
    // フッター（小計・空カート表示）が古いまま塞ぎが解ける（Codex P2）。
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(sections.join(',')), {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('sections ' + res.status);
        return res.json();
      })
      .then(function (map) {
        // HTTP 200 でも個別セクションが null で返ることがある（Section Rendering API）。
        // 頼んだものが1つでも欠けていれば描き直せないので失敗にする（CodeRabbit）。
        var missing = sections.some(function (id) { return !map || typeof map[id] !== 'string'; });
        if (missing) throw new Error('sections incomplete');
        state.sections = map;
        return state;
      });
  }

  function cartSections() {
    return sectionsToRender().map(function (s) { return s.section; }).filter(Boolean);
  }

  function postCart(url, body) {
    var config = typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    var sections = cartSections();
    if (sections.length > 0) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(body);
    return fetch(url, config)
      .then(function (res) { return res.json(); })
      .then(function (state) {
        if (state && (state.errors || state.status)) {
          throw new Error(state.description || state.errors || 'cart request failed');
        }
        return state;
      });
  }

  function changeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  // block は通信しない（判定だけ）。update / remove は /cart/change.js 1回で完結する。
  function applyOp(op) {
    if (op.type === 'block') return Promise.resolve(null);
    if (op.type === 'update') {
      var body = { id: op.key };
      if (op.properties) {
        // 書き戻し（プロパティだけの変更）に quantity を載せない — /cart.js を読んで
        // から届くまでの間に顧客が数量を変えていると、古い数量で上書きしてしまう
        // （Codex P2）。Shopify の /cart/change.js は quantity 省略時に数量を保つ。
        body.properties = op.properties;
        return postCart(changeUrl(), body);
      }
      // 数量を変える操作（excess）は、送る直前に /cart.js を取り直して再計画し、同じ操作
      // （同じ行・同じ目標数量）がまだ計画されている時だけ送る。計画から送信までの間に
      // 顧客がカートを編集していると（例: 本体を 1→2 にした直後）、古い計画の数量で
      // 上書きして編集を失わせる（Codex P2）。延長行だけでなく本体側の変化も拾うため、
      // 行の数量比較ではなく計画の一致で判定する。違えば送らず、次の周回に任せる。
      return fetch('/cart.js')
        .then(function (res) {
          if (!res.ok) throw new Error('cart.js ' + res.status);
          return res.json();
        })
        .then(function (cart) {
          var still = planReconcile(cart).some(function (fresh) {
            return fresh.type === 'update' && fresh.kind === op.kind &&
              fresh.key === op.key && fresh.quantity === op.quantity;
          });
          if (!still) return { skipped: true };
          body.quantity = op.quantity;
          return postCart(changeUrl(), body);
        });
    }
    return postCart(changeUrl(), { id: op.key, quantity: 0 });
  }

  function checkoutButtons() {
    return document.querySelectorAll('button[name="checkout"]');
  }

  // チェックアウトを塞ぐ2つの理由:
  //   pending … 整合を取っている間。延長行を消した直後の Dawn の再描画はチェックアウトを
  //             有効なまま出すので、回線が遅いと /cart.js → /cart/change.js が届く前に
  //             決済へ進め、このスクリプトが防ぐはずの状態がそのまま通る（Codex P1）
  //   blocked … 自動では直せない不整合（partial）が残っている間。顧客が揃えるまで塞ぐ
  // cart-deadline-guard.js と同じ要素を触るので、印と手段を分けて互いに壊さない:
  //   通常ボタン … disabled ＋ data-extension-sync-gate。戻す時はガードの印
  //                （data-deadline-blocked）が無い時だけ有効化する
  //   Shop Pay 等 … ガードは display を切るので、こちらは visibility を切る
  // fail-open: 通信できなくても pending は必ず解く（finally）。blocked は判定結果なので
  // 次の周回（cartUpdate）で再評価される。
  var pending = false;
  var blocked = false;
  // Dawn のカート操作が始まって、その結果の cartUpdate をまだ受け取っていない間。
  // pending とは別に持つ — 走っている最中の reconcile() が終わる時に pending を解いても、
  // 操作中の塞ぎは残す（Codex P1）。対応する cartUpdate（または保険のタイマー）で解く。
  var mutationPending = false;

  // cart-deadline-guard.js が /checkout へ遷移する直前に問い合わせる。押下→在庫検証の
  // 間に延長行が消えると、ガードの署名（本体行のみ）は変わらないまま遷移してしまうため
  // （Codex P1）。
  window.TRCartExtensionSync = {
    isGated: function () { return pending || blocked || mutationPending; }
  };

  function applyGate() {
    var gate = pending || blocked || mutationPending;
    Array.prototype.forEach.call(checkoutButtons(), function (btn) {
      if (gate) {
        btn.disabled = true;
        btn.dataset.extensionSyncGate = 'true';
      } else if (btn.dataset.extensionSyncGate) {
        delete btn.dataset.extensionSyncGate;
        if (!btn.dataset.deadlineBlocked) btn.disabled = false;
      }
    });
    Array.prototype.forEach.call(document.querySelectorAll('.additional-checkout-buttons'), function (el) {
      if (gate) {
        el.style.visibility = 'hidden';
        el.dataset.extensionSyncGate = 'true';
      } else if (el.dataset.extensionSyncGate) {
        delete el.dataset.extensionSyncGate;
        el.style.visibility = '';
      }
    });
    // 整合中（pending）と操作中（mutationPending）は明細の数量入力・±・削除も止める。
    // /cart.js を読んでから /cart/change.js が届くまでの間に顧客が数量を変えると、古い計画で
    // 上書きして編集を失わせる。送る直前の再計画（applyOp）だけでは読み→書きの間が残る
    // ので、書いている間は編集させない（Codex P2）。ただし blocked（部分課金）の間は
    // 止めない — 顧客に数量を揃えてもらうための操作そのものなので（Codex P1）。
    // 元から disabled のものは触らない（印を付けたものだけ戻す）。
    var editGate = pending || mutationPending;
    Array.prototype.forEach.call(
      document.querySelectorAll('cart-items input, cart-items button, cart-drawer-items input, cart-drawer-items button'),
      function (el) {
        if (editGate) {
          if (!el.disabled) {
            el.disabled = true;
            el.dataset.extensionSyncGate = 'true';
          }
        } else if (el.dataset.extensionSyncGate) {
          delete el.dataset.extensionSyncGate;
          el.disabled = false;
        }
      }
    );
  }

  function removeNotices() {
    var existing = document.querySelectorAll('.' + NOTICE_CLASS);
    Array.prototype.forEach.call(existing, function (el) {
      el.parentNode.removeChild(el);
    });
  }

  // チェックアウトボタン直前に案内を出す（cart-deadline-guard.js の通知と同じ位置・
  // 別クラス。あちらは自分のクラスだけ消すのでこの通知は残る）。エラーではないので赤にしない。
  function showNotice(ops) {
    removeNotices();
    var lines = noticeLines(ops);
    if (lines.length === 0) return;
    var blocking = ops.some(function (op) { return op.type === 'block'; });
    Array.prototype.forEach.call(checkoutButtons(), function (btn) {
      var notice = document.createElement('div');
      notice.className = NOTICE_CLASS;
      notice.setAttribute('role', blocking ? 'alert' : 'status');
      notice.style.cssText =
        'margin:0 0 1rem;padding:1rem;border:0.1rem solid rgba(var(--color-foreground),0.3);border-radius:0.4rem;font-size:1.3rem;line-height:1.5;white-space:pre-line;';
      notice.textContent = lines.join('\n');
      btn.parentNode.insertBefore(notice, btn);
    });
  }

  // 計測は「何が起きたか」単位（kind）で1回。語彙は ga4-events.js の
  // cartExtensionSynced のコメントが正本。partial はカートを変えないので周回ごとに
  // 再計画に現れる — 塞いだ瞬間（blocked が false → true）にだけ送る（Codex P2）。
  function track(ops, enteredBlocked) {
    if (!window.TRAnalytics || typeof TRAnalytics.cartExtensionSynced !== 'function') return;
    ops.forEach(function (op) {
      if (op.type === 'error' || op.type === 'refresh') return;
      if (op.type === 'block' && !enteredBlocked) return;
      TRAnalytics.cartExtensionSynced(op.kind, op.title, op.days || 0);
    });
  }

  // cart-deadline-guard.js に案内を作り直させ、他の購読者（カートアイコン等）にも
  // 変更を知らせる。source を自前にしておくと自分の購読では無視できる。
  // 購読者の完了を待てる Promise を返す — Dawn の CartItems / CartDrawerItems は
  // source が 'cart-items' 以外だと onCartUpdate() でセクションを取り直し、ドロワーでは
  // .cart-drawer__footer ごと置き換える（assets/cart.js）。その前に差した案内は消える
  // ので、案内は publish の完了後に差す（Codex P2）。pubsub.js の publish は購読者の
  // 戻り値（onCartUpdate の fetch を含む）を Promise.all で返す。
  function publishCartUpdate(state) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' ||
        !PUB_SUB_EVENTS.cartUpdate) {
      return Promise.resolve();
    }
    try {
      return Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state }))
        .catch(function () { /* 他の購読者の失敗で案内を止めない */ })
        // 購読者（Dawn の onCartUpdate）がフッターを置き換えた後にも塞ぎ直す
        .then(applyGate);
    } catch (e) {
      return Promise.resolve();
    }
  }

  // 失敗時の自動やり直し。/cart/change.js が途中で失敗すると、成功した操作までは
  // 反映済み・残りは未反映のまま（例: 本体2行の書き戻しのうち2行目だけ失敗）になる。
  // 成功分はカートに乗っているので、もう一周すれば残りだけが再計画される（冪等）。
  // 1回だけやり直し、それも失敗したら fail-open で案内を出して塞ぎを解く（Codex P1）。
  var RETRY_DELAY_MS = 1500;
  var retryUsed = false;
  // やり直し前の周回で成功した操作。やり直しの周回では再計画に現れない（もう
  // カートに反映済み）ので、案内と計測に含めるためにここで持ち越す（Codex P2）。
  var carriedOps = [];
  // 次の周回がやり直しかどうか。やり直しで「もう直っていた」（変更は届いたが応答が
  // 落ちた）時は、何も操作しなくても画面が古いままなので描き直す（Codex P2）。
  var retrying = false;
  // やり直し前の周回が始まる時点で塞いでいたか。/cart.js の取得失敗で blocked を
  // リセットした直後のやり直しを「新たに塞いだ」と数えない（Codex P2）。
  var retryWasBlocked = null;

  function reconcile() {
    if (checkoutButtons().length === 0) return;
    if (inFlight) { queued = true; return; }
    inFlight = true;
    pending = true;
    // 前回の判定は捨てる — /cart.js が取れなかった時に古い blocked が残ると、
    // 顧客が揃えた後もチェックアウトが塞がったままになる（Codex P2）。
    var wasBlocked = retryWasBlocked !== null ? retryWasBlocked : blocked;
    retryWasBlocked = null;
    blocked = false;
    applyGate();
    // 成功した操作（案内はこれで組む。計画どおりに終わらなかった操作を
    // 「戻しました」と言わないため — Codex P2）と、その最後の応答。
    var doneOps = carriedOps;
    carriedOps = [];
    var isRetry = retrying;
    retrying = false;
    var lastState = null;
    var plannedOps = [];
    var retryScheduled = false;
    var succeeded = false; // 周回が失敗なしで終わった（無操作も含む）→ やり直しの権利を戻す
    fetch('/cart.js')
      .then(function (res) {
        // 非OK（メンテ画面・エラーJSON等）を空カートとして読まない（CodeRabbit）
        if (!res.ok) throw new Error('cart.js ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var ops = planReconcile(cart);
        plannedOps = ops;
        blocked = ops.some(function (op) { return op.type === 'block'; });
        if (ops.length === 0) {
          succeeded = true;
          if (!isRetry) return null;
          // やり直しに来たらもう整っていた＝前の周回の変更は届いていた。画面だけ
          // 古いので取り直して描き直し、確認を促す。
          return refreshSections(cart).then(function (state) {
            doneOps.push({ type: 'refresh', kind: 'refreshed' });
            // 持ち越した成功済みの操作は実際に起きたので計測する（Codex P2）
            track(doneOps, false);
            lastState = state;
            renderSections(state);
            return publishCartUpdate(state);
          });
        }
        // Shopify のカート変更は直列に投げる（並行だと後着が先着の結果を上書きしうる）
        return ops.reduce(function (p, op) {
          return p.then(function () {
            return applyOp(op).then(function (state) {
              if (state && state.skipped) {
                // 直前の確認でカートが変わっていた。この操作は見送り、次の周回で再計画する
                queued = true;
                return null;
              }
              doneOps.push(op);
              if (state) lastState = state;
              return state;
            }, function (err) {
              // どの操作で止まったかを案内の文面に使う
              if (err && typeof err === 'object') err.failedOp = op;
              throw err;
            });
          });
        }, Promise.resolve()).then(function () {
          succeeded = true;
          // 計測は実際に起きた操作（持ち越し分を含む）だけ
          track(doneOps, blocked && !wasBlocked);
          if (!lastState) return null; // block だけ＝カートは変えていない
          // 最後の応答のセクションでフッターまで描き直し、購読者の再描画
          // （ドロワーはフッターごと置き換える）が終わってから案内を差す。
          // 順序を逆にすると案内が消える。
          renderSections(lastState);
          return publishCartUpdate(lastState);
        });
      })
      .catch(function (err) {
        console.error('cart-extension-sync:', err);
        if (!retryUsed) {
          // 1回だけやり直す。塞ぎは解かず（pending のまま）に待つ。
          // 成功済みの操作は次の周回へ持ち越す（再計画には現れないため）。
          retryUsed = true;
          retryScheduled = true;
          retrying = true;
          retryWasBlocked = wasBlocked;
          carriedOps = doneOps.filter(function (op) { return op.type !== 'block'; });
        } else {
          // fail-open: 直せなくても決済は止めない（ops 側の延長ガードが保険）。
          // 失敗した操作の分だけ表示と請求が食い違ったまま残るので、その旨は伝える。
          // 何が失敗したかで案内を変える（書き戻し以外の失敗に「請求どおりの日程で届く」
          // と言うと、余った延長の請求をそのまま通してしまう — Codex P2）。
          var failedOp = err && err.failedOp;
          // 失敗した操作より後ろの操作は実行されていない。延長行の削除・減量が残って
          // いるなら請求側も未解決なので、「請求どおりの日程（延長なし）で届く」と
          // 言ってはいけない（CodeRabbit）。
          // 成功済みの操作（持ち越し分を含む）は実際に起きたので計測する（Codex P2）
          track(doneOps, false);
          var idx = failedOp ? plannedOps.indexOf(failedOp) : -1;
          var unexecuted = idx >= 0 ? plannedOps.slice(idx + 1) : [];
          var chargeUnresolved = unexecuted.some(function (op) {
            return op.kind === 'orphan' || op.kind === 'excess';
          });
          doneOps.push({ type: 'error', kind: 'error', failedKind: failedOp ? failedOp.kind : null,
            title: failedOp ? failedOp.title : null, chargeUnresolved: chargeUnresolved });
        }
        if (lastState) {
          renderSections(lastState);
          return publishCartUpdate(lastState);
        }
        return null;
      })
      .then(function () {
        if (succeeded) retryUsed = false;
        if (retryScheduled) {
          inFlight = false;
          setTimeout(reconcile, RETRY_DELAY_MS);
          return;
        }
        if (doneOps.length > 0) showNotice(doneOps);
        inFlight = false;
        if (queued) {
          // 走っている間にカートが変わった。塞ぎを解かずに（pending のまま）
          // すぐ次の周回へ — 一瞬でも開けると、その間に決済へ進める。
          queued = false;
          setTimeout(reconcile, 0);
          return;
        }
        pending = false;
        applyGate();
      });
  }

  // 初回は Dawn の cart-items / cart-drawer-items の定義を（上限付きで）待ってから走る。
  // 未定義のまま走っても上の予備でセクションは頼めるが、Dawn 側の再描画（onCartUpdate）
  // にも乗れる方がよい。定義されない構成（要素が無い）では待たない。
  function whenCartComponentsReady() {
    if (typeof customElements === 'undefined' || typeof customElements.whenDefined !== 'function') {
      return Promise.resolve();
    }
    var waits = [];
    if (document.querySelector('cart-items')) waits.push(customElements.whenDefined('cart-items'));
    if (document.querySelector('cart-drawer-items')) waits.push(customElements.whenDefined('cart-drawer-items'));
    if (waits.length === 0) return Promise.resolve();
    return Promise.race([
      Promise.all(waits),
      new Promise(function (resolve) { setTimeout(resolve, 3000); })
    ]);
  }

  function initialReconcile() {
    whenCartComponentsReady().then(reconcile, reconcile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialReconcile);
  } else {
    initialReconcile();
  }

  // Dawn のカート操作（削除・数量変更）が「実際に始まった」時点で先に塞ぐ。Dawn は
  // /cart/change.js の応答が返ってから cartUpdate を発行するので、その間チェックアウトは
  // 有効なまま — 延長行の削除がサーバに届いた直後に決済へ進めると、本体が未課金の
  // 延長を主張したまま通る（Codex P1）。
  //
  // 合図は Dawn 自身のローディング印: CartItems.updateQuantity() は送信の直前に
  // #main-cart-items / #CartDrawer-CartItems へ `cart__items--disabled` を付け、finally で
  // 外す（assets/cart.js enableLoading / disableLoading）。クリックや change を横取りする
  // より正確で、①バリデーションで弾かれて送信されない数量変更では塞がない（Codex P2）、
  // ②応答が遅くても Dawn が終わるまで塞ぎ続け、タイムアウトで勝手に解かない（Codex P1）。
  // 成功なら cartUpdate（購読側で pending に引き継ぐ）が先に来て、印の除去は no-op。
  // 失敗（Dawn は publish しない）なら印の除去で解き、念のため再評価する。
  var LOADING_CLASS = 'cart__items--disabled';
  var LOADING_HOST_IDS = { 'main-cart-items': true, 'CartDrawer-CartItems': true };

  function onLoadingChanged(el) {
    var loading = el.classList.contains(LOADING_CLASS);
    if (loading && !mutationPending) {
      mutationPending = true;
      applyGate();
    } else if (!loading && mutationPending) {
      // cartUpdate が先に来ていればここには来ない（購読側で解いている）。
      // 来ていない＝Dawn 側で失敗。塞ぎを解き、カートを再評価する。
      mutationPending = false;
      reconcile();
    }
  }

  if (typeof MutationObserver === 'function') {
    var loadingObserver = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var el = records[i].target;
        if (el && el.id && LOADING_HOST_IDS[el.id]) onLoadingChanged(el);
      }
    });
    // ドロワーは要素ごと差し替わることがあるので、body 配下の class 変化を見る
    loadingObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === SOURCE) return;
      // 操作の結果が届いた。操作中の塞ぎは pending に引き継ぐ（下で同期的に立てる）。
      mutationPending = false;
      // 顧客の操作（数量変更・削除）で Dawn がフッターを描き直すので、
      // 前回の案内はここで役目を終える。
      if (event && event.source === 'cart-items') removeNotices();
      // Dawn のセクション再描画完了を待ってから走る。その一瞬も塞いだままにする
      // （reconcile() が pending を立て直し、終わりに解く）。
      pending = true;
      applyGate();
      setTimeout(reconcile, 0);
    });
  }
})();
